import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
  Mutation,
  MutationSetIsAdminForGroupArgs,
  MutationSetIsQrCodeVisitInviterForGroupArgs,
  Query,
  QueryGetVisitorGroupsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { SET_IS_ADMIN_FOR_GROUP, SET_IS_QR_CODE_VISIT_INVITER_FOR_GROUP } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_WITH_PERMISSIONS } from 'graphql/queries';
import { useEffect } from 'react';
import {
  setIsAdminGroupAction,
  setIsQrCodeVisitInviterGroupAction,
  setPermissionsGroups
} from 'state/actions/permissions';
import { permissionsVar } from 'state/vars';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { BaseHookProps } from '../shared/types';

const usePermissions = ({ handleFetchError }: BaseHookProps) => {
  const groupsWithPermissions = useReactiveVar(permissionsVar);

  const [getGroupsWithPermissions, { loading }] = useLazyQuery<
    Pick<Query, 'getVisitorGroupsForTenant'>,
    QueryGetVisitorGroupsForTenantArgs
  >(GET_VISITOR_GROUPS_WITH_PERMISSIONS, {
    onCompleted: (data) => {
      setPermissionsGroups(data.getVisitorGroupsForTenant);
    },
    onError: () => {
      handleFetchError('Error while fetching visitor groups');
    }
  });

  useEffect(() => {
    getGroupsWithPermissions({
      variables: {
        filter: { type: VisitorGroupType.AzureAd }
      }
    });
  }, [getGroupsWithPermissions]);

  const [setIsAdminGroupMutation, { loading: setIsAdminGroupLoading }] = useMutation<
    Pick<Mutation, 'setIsAdminForGroup'>,
    MutationSetIsAdminForGroupArgs
  >(SET_IS_ADMIN_FOR_GROUP, {
    onCompleted: (data) => {
      setIsAdminGroupAction(data.setIsAdminForGroup.id);
    },
    onError: () => {
      handleFetchError('Error while setting group admin');
    }
  });

  const setIsAdminGroup = async (visitorGroupId: string, isAdmin: boolean) => {
    const { data } = await setIsAdminGroupMutation({
      variables: { visitorGroupId, isAdmin },
      update(cache, { data: setIsAdminGroupData }) {
        const updatedGroups = groupsWithPermissions.map((group) => {
          if (group.id === setIsAdminGroupData?.setIsAdminForGroup.id)
            return {
              ...group,
              isAdminGroup: isAdmin
            };
          return group;
        });

        cache.writeQuery({
          query: GET_VISITOR_GROUPS_WITH_PERMISSIONS,
          variables: {
            filter: { type: VisitorGroupType.AzureAd }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  const [setIsQrCodeVisitInvitorGroupMutation, { loading: setIsQrCodeVisitInvitorGroupLoading }] = useMutation<
    Pick<Mutation, 'setIsQrCodeVisitInviterForGroup'>,
    MutationSetIsQrCodeVisitInviterForGroupArgs
  >(SET_IS_QR_CODE_VISIT_INVITER_FOR_GROUP, {
    onCompleted: (data) => {
      setIsQrCodeVisitInviterGroupAction(data.setIsQrCodeVisitInviterForGroup.id);
    },
    onError: () => {
      handleFetchError('Error while setting group admin');
    }
  });

  const setIsQrCodeVisitInviterGroup = async (visitorGroupId: string, isQrCodeVisitInviter: boolean) => {
    const { data } = await setIsQrCodeVisitInvitorGroupMutation({
      variables: { visitorGroupId, isQrCodeVisitInviter },
      update(cache, { data: setIsQrCodeVisitInviterGroupData }) {
        const updatedGroups = groupsWithPermissions.map((group) => {
          if (group.id === setIsQrCodeVisitInviterGroupData?.setIsQrCodeVisitInviterForGroup.id)
            return {
              ...group,
              isQrCodeVisitInviterGroup: isQrCodeVisitInviter
            };
          return group;
        });

        cache.writeQuery({
          query: GET_VISITOR_GROUPS_WITH_PERMISSIONS,
          variables: {
            filter: { type: VisitorGroupType.AzureAd }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  return {
    groupsWithPermissions,
    loading,
    setIsAdminGroup,
    setIsAdminGroupLoading,
    setIsQrCodeVisitInviterGroup,
    setIsQrCodeVisitInvitorGroupLoading
  };
};

export default usePermissions;
